import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { appStorageKeys } from '~/lib/query-keys';

/**
 * FRONT-3007. How full an app's data storage is and when it fills at the
 * current pace — `GET /api/admin/apps/:slug/storage`, owner-only (404 for
 * anyone else). The same shape rides on each of the owner's rows in the Apps
 * list as `storage`.
 */
export interface AppStorage {
  records: number;
  maxRecords: number;
  bytes: number;
  maxBytes: number;
  recordsPercent: number;
  bytesPercent: number;
  /** The fuller of the two caps, unclamped. */
  percent: number;
  dailyRecords: number;
  rateSource: '7d' | '24h' | 'none';
  /** Net growth from daily size snapshots, or creation dates until a week of them exists. */
  growthBasis: 'snapshots' | 'created';
  limitingCap: 'records' | 'bytes';
  full: boolean;
  daysToFull: number | null;
  /** ISO instant; null when nothing is growing or it is already full. */
  expectedFullAt: string | null;
  /** 0 fine · 1 full within 14 days · 2 at 90% · 3 full. */
  level: 0 | 1 | 2 | 3;
}

export function appStoragePath(slug: string): string {
  return `/api/admin/apps/${encodeURIComponent(slug)}/storage`;
}

export function useAppStorage(slug: string) {
  return useQuery<AppStorage>({
    queryKey: appStorageKeys.detail(slug),
    queryFn: async () => (await api.get<{ storage: AppStorage }>(appStoragePath(slug))).storage,
    enabled: !!slug,
    staleTime: 60_000,
  });
}
